import React, { useState, useEffect, useMemo } from "react";
import { Modal, Button, Form, InputGroup, Alert } from "react-bootstrap";
import {
  resolveAreaFormula,
  formulaDimensions,
  computeMarineArea,
  evaluateAreaFormula,
  lineTotal,
  roundArea,
  AREA_VAR_LABELS,
  estimateFreeboardIn,
  areaVarWarning,
} from "../../util/marineCalc";
import { getMarineRate, formatArea, marineAttrsOf, marineUnit, marineUnitLabel } from "../../util/marineService";
import { formulaText, formulaSubstituted, isTrivialFormula } from "../../util/areaFormulaDisplay";
import DurationInput from "../common/DurationInput";

/**
 * Area calculator for a marine service line.
 *
 * A standalone component by design — deliberately NOT derived from
 * addCustomCut. That modal captures one price for a service that has none;
 * this is a live calculator over a variable set driven by the service's own
 * area formula, with a computed-vs-override distinction.
 *
 * Inputs are driven by `formulaDimensions()` rather than a hardcoded coverage
 * map, so when the server ships richer formulas this renders them with no
 * change here.
 *
 * `hull_height_in` is null on every feed-supplied boat — no feed carries
 * freeboard — so for hull coverage the estimator is prompted for it.
 */
export const MarineCalculatorModal = ({ show, service, vehicle, userType, onConfirm, onCancel, reopen = false }) => {
  // A dealer reaches this dialog only when something cannot be derived — the
  // fully-determined case is priced and added without it. So everything they
  // CAN'T change is shown as text, and only the field that actually needs an
  // answer is editable. Waste is hidden outright rather than locked: it is the
  // installer's material assumption, and putting it in front of a dealer invites
  // a negotiation about how the installer works rather than about the price.
  const isDealer = userType === "dealer";
  // Reads either shape — nested (fresh from the wizard) or flat (a saved boat
  // re-fetched from the server, which stores the fields at the top level).
  const attrs = marineAttrsOf(vehicle);
  const rate = getMarineRate(service) ?? 0;
  // The unit this service is priced in. A LINEAR_LOA service computes CEIL(LOA)
  // in FEET, not square feet, so every quantity label in this modal keys off it.
  const unit = marineUnit(service);
  const uLabel = marineUnitLabel(service) || "sq ft";

  // Resolve the formula once per service. A service whose basis this version
  // does not recognise fails here, visibly, rather than producing a number.
  const { formula, formulaError } = useMemo(() => {
    try {
      return { formula: resolveAreaFormula(service), formulaError: null };
    } catch (err) {
      return { formula: null, formulaError: err.message };
    }
  }, [service]);

  const dimensions = useMemo(() => formulaDimensions(formula), [formula]);

  const serviceWastePct =
    service && service.waste_pct != null ? Number(service.waste_pct) : 0;
  // Catalog default. Marine duration is WHOLE-JOB and never scaled by area —
  // a bigger hull is more film, not proportionally more days — so the estimator
  // adjusts it here rather than it being derived.
  const serviceDurationDays =
    service && Number(service.duration_days) > 0 ? Number(service.duration_days) : 1;

  const [values, setValues] = useState({});
  const [wasteInput, setWasteInput] = useState("");
  const [durationInput, setDurationInput] = useState("");
  // The single area the line is priced on. Seeded from the calculation and
  // kept in step with it until the estimator types their own number, after
  // which it is theirs and recalculation stops touching it.
  const [coverageArea, setCoverageArea] = useState("");
  const [areaTouched, setAreaTouched] = useState(false);
  // Which seeded dimensions were ESTIMATED rather than read off the boat. A
  // derived freeboard prices the job exactly like a measured one, so the
  // estimator has to be able to tell them apart.
  const [derivedDims, setDerivedDims] = useState({});

  // What this line was PRICED with, when the calculator is being reopened on a
  // line that already exists. The dimensions the estimator actually used are
  // the right starting point for a recalculation — they may differ from the
  // boat's reference data (a measured freeboard, a corrected beam), and on an
  // edit-mode reopen the boat row is often all the client has, sometimes with
  // nothing on it. Empty object on a fresh add, so the boat stays the source.
  const priced = reopen && service ? (service.marine_variables || service.marineVariables || {}) : {};
  // The area the line is currently priced at. `quantity` on an area line is
  // CEIL(sq ft) — the same number the Billable Area field holds.
  const pricedArea =
    reopen && service && Number(service.quantity) > 0 ? String(Number(service.quantity)) : "";

  // Pre-fill each time the modal opens: the line's own values first when
  // reopening, then the boat, then a derived freeboard.
  useEffect(() => {
    if (!show) return;
    const seed = {};
    const derived = {};
    dimensions.forEach((dim) => {
      const fromLine = priced[dim];
      if (fromLine !== null && fromLine !== undefined && String(fromLine) !== "") {
        seed[dim] = String(fromLine);
        // A freeboard the estimator never typed was flagged when the line was
        // confirmed; carry that forward so reopening cannot launder an estimate
        // into a measurement.
        if (dim === "hull_height_in" && priced.hull_height_estimated) derived[dim] = true;
        return;
      }
      const v = attrs[dim];
      if (v !== null && v !== undefined && String(v) !== "") {
        seed[dim] = String(v);
        return;
      }
      // No feed carries freeboard, so most boats reach here with the field
      // empty. Estimate it from length rather than leaving the estimator with
      // nothing to anchor on — and record that it was derived, so the note
      // below can say so. Any other missing dimension stays blank.
      if (dim === "hull_height_in") {
        const est = estimateFreeboardIn(attrs.loa_ft);
        if (est !== null) {
          seed[dim] = String(est);
          derived[dim] = true;
          return;
        }
      }
      seed[dim] = "";
    });
    if (reopen && dimensions.length > 0 && dimensions.every((d) => seed[d] === "")) {
      console.warn(
        "[Marine] Reopened an existing line with no dimensions to seed from — neither the line nor the craft carries them.",
        { dimensions, lineVariables: priced, craftAttributes: attrs }
      );
    }
    setValues(seed);
    setDerivedDims(derived);
    setWasteInput(String(serviceWastePct));
    setDurationInput(String(serviceDurationDays));
    // A reopened line opens showing the area it is priced at, not a blank
    // field. It is replaced by the recalculation as soon as the dimensions
    // yield one — unless the estimator had overridden it, which is a number
    // only they can have chosen and must not be silently recomputed away.
    setCoverageArea(pricedArea);
    setAreaTouched(reopen && Boolean(service && (service.marine_area_overridden || service.marineAreaOverridden)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, service]);

  const numericValues = useMemo(() => {
    const out = {};
    Object.keys(values).forEach((k) => {
      if (values[k] !== "" && values[k] !== null) out[k] = Number(values[k]);
    });
    return out;
  }, [values]);

  // Waste is a live parameter, not a fixed property of the catalog row — the
  // estimator can dial it for this job. The service is shadowed rather than
  // mutated so computeMarineArea keeps its single waste source.
  const wastePct = useMemo(() => {
    const n = Number(wasteInput);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }, [wasteInput]);

  const pricingService = useMemo(
    () => ({ ...(service || {}), waste_pct: wastePct }),
    [service, wastePct]
  );

  // Live recompute. computeMarineArea throws on a missing dimension — that is
  // the normal state before the estimator fills freeboard in, so it renders as
  // guidance rather than an error.
  const { computedArea, computeError } = useMemo(() => {
    if (!formula) return { computedArea: null, computeError: formulaError };
    try {
      return { computedArea: computeMarineArea(pricingService, numericValues), computeError: null };
    } catch (err) {
      return { computedArea: null, computeError: err.message };
    }
  }, [formula, formulaError, pricingService, numericValues]);

  // Mirror the calculation into the coverage field until the estimator claims
  // it. With nothing computable, fall back to the seeded area rather than
  // blanking the field — on a reopen that seed is the price the line is
  // currently carrying, and clearing it would make a dialog the estimator only
  // looked at appear to have lost the number. Empty on a fresh add, so this is
  // the same clear-to-blank as before.
  useEffect(() => {
    if (!show || areaTouched) return;
    setCoverageArea(computedArea == null ? pricedArea : String(computedArea));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, areaTouched, computedArea, pricedArea]);

  // What the formula alone yields, before factor and waste — shown so the
  // estimator can see where the number came from.
  const rawArea = useMemo(() => {
    if (!formula) return null;
    try {
      return evaluateAreaFormula(formula, numericValues);
    } catch {
      return null;
    }
  }, [formula, numericValues]);

  const durationDays = Number(durationInput);
  const durationValid = Number.isFinite(durationDays) && durationDays > 0;

  // The coverage field is the sole source of the priced area — always.
  const areaEntered = String(coverageArea).trim() !== "";
  const finalArea = areaEntered ? roundArea(Number(coverageArea)) : null;
  const overridden = finalArea != null && finalArea !== computedArea;
  const total = finalArea == null ? null : lineTotal(finalArea, rate);
  const canConfirm = finalArea != null && rate > 0 && durationValid;

  const handleValueChange = (dim, v) => {
    setValues((prev) => ({ ...prev, [dim]: v }));
    // Typing over an estimate makes it the estimator's number, so the
    // "estimated" note must go with it — leaving it up would understate a value
    // that was actually measured.
    setDerivedDims((prev) => (prev[dim] ? { ...prev, [dim]: false } : prev));
  };

  const handleAreaChange = (v) => {
    setAreaTouched(true);
    setCoverageArea(v);
  };

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm({
      area: finalArea,
      rate,
      total,
      overridden,
      // Flag a freeboard still holding the derived seed. `learnedHullHeight`
      // skips it, so an estimate can never be written back to the boat model as
      // if it had been measured. Cleared the moment the estimator types a value.
      variables: derivedDims.hull_height_in
        ? { ...numericValues, hull_height_estimated: true }
        : numericValues,
      wastePct,
      durationDays,
    });
  };

  const factor = service && service.area_factor != null ? Number(service.area_factor) : 1;

  // The derivation, shown at the top so the estimator can see WHICH input to
  // change. Hidden for a single-variable formula (MEASURED_AREA): "Area (sq ft)"
  // explains nothing about how "Area (sq ft)" was arrived at.
  const showDerivation = Boolean(formula) && !isTrivialFormula(formula);
  const formulaLabel = showDerivation ? formulaText(formula, AREA_VAR_LABELS) : '';
  const formulaNumbers = showDerivation ? formulaSubstituted(formula, numericValues) : '';

  return (
    <Modal show={show} onHide={onCancel} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Calculate area — {service?.description}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {showDerivation && formulaLabel && (
          <Alert variant="info" className="py-2 mb-3">
            <div className="small font-weight-bold mb-1">How this area is calculated</div>
            <div className="small">{formulaLabel}</div>
            {formulaNumbers && rawArea != null && (
              <div className="small mt-1">
                {formulaNumbers} = <strong>{rawArea.toFixed(2)} sq ft</strong>
                {factor !== 1 && `, x ${factor} factor`}
                {wastePct > 0 && `, +${wastePct}% waste`}
              </div>
            )}
          </Alert>
        )}

        {formulaError && (
          <Alert variant="warning">
            <strong>Automatic calculation unavailable.</strong>
            <div className="small mt-1">{formulaError}</div>
            <div className="small mt-2">
              Enter the area manually below to price this service.
            </div>
          </Alert>
        )}

        {!formulaError && (
          <>
            {dimensions.map((dim) => (
              <Form.Group key={dim}>
                <Form.Label>
                  {AREA_VAR_LABELS[dim] || dim} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  step="any"
                  min="0"
                  value={values[dim] ?? ""}
                  onChange={(e) => handleValueChange(dim, e.target.value)}
                  placeholder={attrs[dim] == null ? "Measure or estimate" : undefined}
                  // A dealer edits only what nothing could supply. A dimension
                  // that already has a value — from reference data or the
                  // freeboard estimate — is the installer's to change, not
                  // theirs.
                  readOnly={isDealer && String(values[dim] ?? "") !== ""}
                  plaintext={isDealer && String(values[dim] ?? "") !== ""}
                />
                {derivedDims[dim] && (
                  // Says so plainly. A derived freeboard prices the hull exactly
                  // like a measured one, and freeboard is the dimension a
                  // length-based rule gets most wrong — a centre console and a
                  // sedan bridge of the same length differ hugely.
                  <Form.Text className="text-muted">
                    Estimated from length overall. Measure the boat if you can.
                  </Form.Text>
                )}
                {areaVarWarning(dim, values[dim]) && (
                  <Form.Text className="text-danger">
                    {areaVarWarning(dim, values[dim])}
                  </Form.Text>
                )}
              </Form.Group>
            ))}

            <Form.Group hidden={isDealer}>
              <Form.Label>Waste</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  step="any"
                  min="0"
                  value={wasteInput}
                  onChange={(e) => setWasteInput(e.target.value)}
                />
                <InputGroup.Append>
                  <InputGroup.Text>%</InputGroup.Text>
                </InputGroup.Append>
              </InputGroup>
            </Form.Group>

            <hr />

            {computeError && !areaEntered && (
              <Alert variant="warning" className="py-2">{computeError}</Alert>
            )}

            {computedArea != null && (
              <p className="mb-2">
                Calculated:{" "}
                <strong>{formatArea(computedArea, unit)}</strong>
                {rawArea != null && (
                  <span className="text-muted small">
                    {"  ("}
                    {rawArea.toFixed(2)} {uLabel}
                    {factor !== 1 && ` × ${factor}`}
                    {wastePct > 0 && ` + ${wastePct}% waste`}
                    {", rounded up)"}
                  </span>
                )}
              </p>
            )}

          </>
        )}

        <Form.Group>
              {/* "Billable Area", not "Calculated Area" — this is the number the
                  customer is charged for. It already carries area_factor and
                  waste and is rounded up (see computeMarineArea), and the line
                  total is simply this x the rate; nothing downstream re-applies
                  either. Naming it "calculated" undersold it as an intermediate
                  value.

                  Distinct from the SERVICE's "Coverage Area", which is the
                  DEFINITION — which part of the asset, fixed at create. Two
                  different things; they must not share a label. */}
              <Form.Label>
                Billable Area <span className="text-danger">*</span>
              </Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  step="1"
                  min="1"
                  value={coverageArea}
                  onChange={(e) => handleAreaChange(e.target.value)}
                  placeholder="Enter the area for this job"
                  // Derived from the dimensions above, which the dealer either
                  // cannot change or has just supplied. Overriding the computed
                  // area is an installer's call.
                  readOnly={isDealer}
                  plaintext={isDealer}
                />
                <InputGroup.Append>
                  <InputGroup.Text>{uLabel}</InputGroup.Text>
                </InputGroup.Append>
              </InputGroup>
              {overridden && computedArea != null && (
                <Form.Text className="text-muted">
                  Calculated {formatArea(computedArea, unit)}.
                </Form.Text>
              )}
        </Form.Group>

        <Form.Group>
              <Form.Label>
                Duration <span className="text-danger">*</span>
              </Form.Label>
              <DurationInput
                value={durationInput}
                // Books calendar time at the installer's shop. Not the
                // dealer's to set.
                readOnly={isDealer}
                plaintext={isDealer}
                onChange={(days) => setDurationInput(days)}
                isInvalid={durationInput !== "" && !durationValid}
                hint
              />
              <Form.Text className="text-muted">
                Books this much time on the calendar. Whole job — not per square foot.
              </Form.Text>
        </Form.Group>

            {finalArea != null && (
              <Alert variant="light" className="mt-3 mb-0 border">
                <strong>
                  {formatArea(finalArea, unit)} @ ${rate.toFixed(2)}/{uLabel} = ${total.toFixed(2)}
                </strong>
              </Alert>
            )}

            {rate <= 0 && (
              <Alert variant="danger" className="mt-3 mb-0">
                This service has no $/sq ft rate configured, so it cannot be priced.
              </Alert>
            )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button variant="primary" onClick={handleConfirm} disabled={!canConfirm}>
          {reopen ? "Update line" : "Add to estimate"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MarineCalculatorModal;
